import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { APP_RUNTIME_CONFIG } from '@core/auth/app-config.model';

import { API_ERROR_HANDLED } from './api-error.interceptor';
import { PendingFileDeletionDto, RecoveredFileDto, RescueFileDto } from './file-deletions.dto';

/**
 * The file-deletion queue — `docs/backend-contract-file-deletions.md`.
 *
 * All three are **administrator-only, gated on `users-access`** rather than on
 * whatever the file was attached under: the queue spans every file removed
 * anywhere in the business, so being able to author policies must not reveal what
 * was deleted from seats.
 */
@Injectable({ providedIn: 'root' })
export class FileDeletionsApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(APP_RUNTIME_CONFIG);

  available(): boolean {
    return !!this.config.apiUrl;
  }

  private url(path: string): string {
    return `${this.config.apiUrl.replace(/\/$/, '')}/admin${path}`;
  }

  /** Pending destruction, soonest first. The server's order is the right one. */
  listPending(): Observable<PendingFileDeletionDto[]> {
    return this.http.get<PendingFileDeletionDto[]>(this.url('/file-deletions'));
  }

  /** Kept, newest rescue first. */
  listRecovered(): Observable<RecoveredFileDto[]> {
    return this.http.get<RecoveredFileDto[]>(this.url('/recovered-files'));
  }

  /**
   * Keep one. Addressed by `attachmentGuid`.
   *
   * `handled`: both refusals mean something specific and the screen says it
   * itself — **404** is the nightly job having run while the queue was open, and
   * **409** is another administrator having kept it first. Neither is a fault
   * worth a generic dialog.
   */
  rescue(attachmentGuid: string, notes: string): Observable<RecoveredFileDto> {
    const body: RescueFileDto = notes.trim() ? { notes: notes.trim() } : {};
    return this.http.post<RecoveredFileDto>(
      this.url(`/file-deletions/${encodeURIComponent(attachmentGuid)}/rescue`),
      body,
      { context: new HttpContext().set(API_ERROR_HANDLED, true) },
    );
  }
}
